import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'ramda';
import type {
  PredefinedFilter,
  RowFilterCondition,
  RowFilterConditionOrNil,
  ValueOrUpdater,
} from '@qre-mailing/smart-table';

import { defineStyles, theme } from '../../theme';

import {
  isEmptyFilter,
  isPredefinedFilterActive,
  mergePredefinedFilters,
  removePredefinedFilterFromCondition,
} from './predefinedFilterMerge';

export interface PredefinedFiltersProps {
  predefinedFilters: PredefinedFilter[];
  currentFilter?: RowFilterConditionOrNil;
  setFilter: (valueOrUpdater: ValueOrUpdater<RowFilterConditionOrNil>) => void;
}

const styles = defineStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 1,
    rowGap: 0.5,
    mt: 0.5,
  },
  chip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.25,
    width: '100%',
    padding: '6px 4px',
    borderRadius: 1,
    minHeight: 0,
    transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease',
    '&:focus-visible': {
      outline: `2px solid ${theme.vars.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
  },
  chipInactive: {
    backgroundColor: theme.vars.palette.grey[50],
    border: '1px solid',
    borderColor: theme.vars.palette.grey[200],
    '&:hover': {
      boxShadow: `inset 0 0 0 1.5px ${theme.vars.palette.primary.main}`,
    },
  },
  chipActive: {
    backgroundColor: theme.vars.palette.primary.main,
    border: 'none',
    color: theme.vars.palette.common.white,
    '&:hover': {
      backgroundColor: theme.vars.palette.primary.main,
    },
  },
  chipCombineMode: {
    boxShadow: `inset 0 0 0 1.5px ${theme.vars.palette.primary.main}`,
  },
  label: {
    color: theme.vars.palette.text.primary,
    fontSize: 12,
    fontWeight: 500,
  },
  labelActive: {
    color: theme.vars.palette.common.white,
    fontSize: 12,
    fontWeight: 500,
  },
  count: {
    color: theme.vars.palette.text.secondary,
    fontSize: 10,
    fontWeight: 400,
  },
  countActive: {
    color: theme.vars.palette.common.white,
    fontSize: 10,
    fontWeight: 500,
    opacity: 0.95,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1,
    fontFamily: 'inherit',
  },
  plusBadge: {
    backgroundColor: theme.vars.palette.primary.main,
    color: theme.vars.palette.common.white,
  },
  minusBadge: {
    backgroundColor: theme.vars.palette.warning.main,
    color: theme.vars.palette.common.white,
  },
  minusBadgeSymbol: {
    display: 'block',
    lineHeight: 1,
    transform: 'translateY(0.5px)',
  },
  chipWrapper: {
    position: 'relative',
    width: 84,
  },
  hint: {
    width: '100%',
    fontSize: 12,
    color: theme.vars.palette.text.secondary,
    fontWeight: 400,
    mb: 0,
    pl: 0.5,
  },
});

export function PredefinedFilters({ predefinedFilters, currentFilter, setFilter }: PredefinedFiltersProps) {
  const intl = useIntl();
  const [isAltHeld, setIsAltHeld] = useState(false);

  const handleFilterClick = useCallback(
    (filter: RowFilterCondition, combineMode: boolean) => {
      if (!combineMode) {
        setFilter(filter);
      } else {
        setFilter((prev) => {
          if (prev && isPredefinedFilterActive(prev, filter)) {
            return removePredefinedFilterFromCondition(prev, filter);
          }
          return mergePredefinedFilters(prev, filter);
        });
      }
    },
    [setFilter]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) setIsAltHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.altKey) setIsAltHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  if (isEmpty(predefinedFilters)) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      <Typography sx={styles.hint} variant="caption" component="span">
        <FormattedMessage
          id="filter.predefinedFilters.hint"
          defaultMessage="Click to select filter, or use ALT + click to combine filter"
        />
      </Typography>
      {predefinedFilters?.map((filter) => {
        const isActive = isPredefinedFilterActive(currentFilter, filter.filter);
        const isFilterEmpty = isEmptyFilter(filter.filter);
        const showPlusBadge = !isFilterEmpty && isAltHeld && !isActive;
        const showMinusBadge = !isFilterEmpty && isAltHeld && isActive;

        return (
          <Box key={filter.name} sx={styles.chipWrapper}>
            <Tooltip title={filter.description}>
              <Button
                sx={[
                  styles.chip,
                  isActive ? styles.chipActive : styles.chipInactive,
                  !isFilterEmpty && isAltHeld && !isActive && styles.chipCombineMode,
                  isFilterEmpty &&
                    isAltHeld && {
                      cursor: 'default',
                      '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: isActive ? theme.vars.palette.primary.main : theme.vars.palette.grey[50],
                      },
                    },
                ]}
                onClick={(e) => handleFilterClick(filter.filter, e.altKey)}
              >
                <Typography sx={isActive ? styles.labelActive : styles.label}>{filter.name}</Typography>
                <Typography sx={isActive ? styles.countActive : styles.count}>
                  {intl.formatNumber(filter.sum)}
                </Typography>
              </Button>
            </Tooltip>
            {showPlusBadge && <Box sx={[styles.badge, styles.plusBadge]}>+</Box>}
            {showMinusBadge && (
              <Box sx={[styles.badge, styles.minusBadge]}>
                <Box component="span" sx={styles.minusBadgeSymbol}>
                  −
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
